import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/pro-solid-svg-icons"
import { pickAccessibleTextColor } from "../../utils"
import "./profile-image.css"

const stringToInitial = (str) => {
  return str?.trim().charAt(0).toUpperCase() || ""
}

export const defaultProfileImageColor = "#CAFE13"

/**
 * Display a user's profile image. If they have no profile image, display their
 * initials. 
 * @prop {*} profile - User's profile object
 * @prop {string} className - For custom styles
 * @prop {string} potentialReplacementURL - URL of image that, if provided, will be displayed instead of profile image. If "", will display initials instead.
 * @prop {string} potentialReplacementColor - hex code that, if provided, will be displayed instead of the user's saved color
 * @prop {boolean} isLoading - Whether to display a loading spinner
 * @returns {JSX}
 */
const ProfileImage = ({profile, className = "", potentialReplacementURL = null, potentialReplacementColor, isLoading = false}) => {
  if (!profile?.email || isLoading) {
    return (
      <div className={`profile-image profile-image-loading ${className}`}>
        <FontAwesomeIcon className="loading-spinner" icon={faSpinner} spin />
      </div>
    )
  }

  const { profileImageUrl, firstName, lastName, email, profileColor } = profile

  if (potentialReplacementURL || (potentialReplacementURL === null && profileImageUrl)) {
    return (
      <img 
        className={`profile-image profile-image-loaded ${className}`}
        alt={`${firstName || ''} ${lastName || ''}`.trim() || email || 'User profile photo'}
        src={potentialReplacementURL || profileImageUrl}
      />
    )
  }

  let firstInitial = stringToInitial(firstName)
  const lastInitial = stringToInitial(lastName)
  if (!firstInitial && !lastInitial) {
    firstInitial = stringToInitial(email)
  }
  const profileText = `${firstInitial}${lastInitial}`

  const backgroundColor = potentialReplacementColor || profileColor || defaultProfileImageColor
  const fontColor = pickAccessibleTextColor(backgroundColor)

  return (
    <div 
      className={`profile-image profile-image-default ${className}`} 
      style={{backgroundColor: backgroundColor, color: fontColor}}
    >
      <div className="initials">{profileText}</div>
    </div>
  )
}

export default ProfileImage
